import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logAuthEvent } from '@/lib/auth-telemetry';

type AppRole = 'super_admin' | 'agency_staff' | 'brand_client' | 'creator';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [roleLoaded, setRoleLoaded] = useState(false);

  const fetchRole = async (userId: string) => {
    try {
      const [{ data: roleRow }, { data: profileRow }] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', userId).limit(1).maybeSingle(),
        supabase.from('profiles').select('force_logout_at').eq('id', userId).maybeSingle(),
      ]);

      // Tier 4c — honour Super Admin force-logout kill switch.
      if (profileRow?.force_logout_at) {
        const forced = new Date(profileRow.force_logout_at).getTime();
        const tokenIat = (session?.user as any)?.created_at
          ? new Date((session!.user as any).created_at).getTime()
          : 0;
        if (forced > tokenIat - 60_000) {
          await supabase.auth.signOut();
          window.location.replace('/login?reason=session_revoked');
          return;
        }
      }

      const resolved = (roleRow?.role as AppRole) || null;
      console.info('[auth] profile_loaded', { userId, role: resolved });
      void logAuthEvent({
        event: 'profile_loaded',
        success: true,
        userId,
        role: resolved,
        email: session?.user?.email ?? null,
      });
      setRole(resolved);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[auth] profile_load_failed', err);
      void logAuthEvent({
        event: 'profile_load_failed',
        success: false,
        userId,
        errorMessage: msg,
      });
      setRole(null);
    } finally {
      setRoleLoaded(true);
    }
  };

  // Track the user id whose role we've already loaded so that benign auth
  // events (TOKEN_REFRESHED / USER_UPDATED / same-user SIGNED_IN re-emits on
  // window refocus) do NOT flip `loading` back to true. That flicker would
  // unmount the routed tree and wipe local component state — active tab,
  // open dialogs, selected records, in-progress form drafts. We only treat a
  // user-id change (real sign-in / sign-out / account switch) as needing a
  // role re-fetch.
  const loadedRoleForUserId = useRef<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        const nextUserId = nextSession?.user?.id ?? null;
        const prevUserId = loadedRoleForUserId.current;

        // Always keep session/user in sync — token refreshes carry a new JWT
        // that the supabase client needs to attach to subsequent requests.
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
        setSessionLoaded(true);

        if (!nextUserId) {
          loadedRoleForUserId.current = null;
          setRole(null);
          setRoleLoaded(true);
          return;
        }

        if (nextUserId !== prevUserId) {
          // Real account switch — refetch role.
          loadedRoleForUserId.current = nextUserId;
          setRoleLoaded(false);
          setTimeout(() => fetchRole(nextUserId), 0);
        }
        // else: same user, just a token refresh or USER_UPDATED event —
        // leave role + loading untouched so the UI doesn't flash.
      }
    );

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      setSessionLoaded(true);
      if (existing?.user) {
        loadedRoleForUserId.current = existing.user.id;
        fetchRole(existing.user.id);
      } else {
        setRoleLoaded(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    window.location.replace('/login');
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  // Stay in loading until both session AND role are resolved (when a user exists).
  const loading = !sessionLoaded || (!!user && !roleLoaded);

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
